import React from "react";
import { Routes, Route, Navigate, useParams } from "react-router-dom";
import DashboardLayout from "./components/DashboardLayout";
import InstallApp from "./pages/InstallApp";

// Auth pages
import { LoginPage } from "./pages/auth/Login";
import ForgotPasswordPage from "./pages/auth/ForgetPassword";
import ForgotPasswordSuccessPage from "./pages/auth/ForgotPasswordSuccess";
import InvalidTokenPage from "./pages/auth/InvalidTokenPage";
import TokenVerificationPage from "./pages/auth/TokenVerificationPage";
import SubscriptionExpired from "./pages/Subscribe";

// Core pages
import { DashboardPage } from "./pages/Dashboard";
import Drinks from "./pages/Service/Drinks";
import Foods from "./pages/Service/Foods";
import Games from "./pages/Service/Games";
import Laundry from "./pages/Service/Laundries";
import Orders from "./pages/Orders";
import { Rooms } from "./pages/bookings/Rooms";
import BookRoomPage from "./pages/bookings/BookRoom";
import User from "./pages/staff/StaffList";
import CreateUser from "./pages/staff/CreateUpdateStaff";
import BookingPage from "./pages/bookings/Bookings";
import InventoryPage from "./pages/inventory/Inventory";
import FoodDrinkInventoryPage from "./pages/inventory/FoodDrinkInventory";
import GameLaundryInventoryPage from "./pages/inventory/GameLaundryInventory";
import RoomsInventoryPage from "./pages/inventory/RoomInventory";
import SettingsPage from "./pages/Settings/SettingList";
import PrintSettings from "./pages/Settings/PrintSetting";
import DailySales from "./pages/accounting/DailySales";
import NotFoundPage from "./pages/NotFound";
import GuestPage from "./pages/bookings/Guests";
import AssignStaffIDPage from "./pages/staff/StaffId";
import TestPage from "./pages/Test";

// Contexts
import { CartProvider } from "./context/CartContext";
import { ModalProvider } from "./context/ConfirmModalContext";
import { NetworkProvider } from "./context/NetworkContext";
import { OrderProvider } from "./context/OrderContext";
import { ToastProvider } from "./context/ToastContext";

// Auth protection
import { ProtectedRoute } from "./components/ProtectedRoute";
import Roster from "./pages/staff/Roster";
import StaffProfile from "./pages/staff/Profile";
import Expenditures from "./pages/accounting/Expenditures";
import Sales from "./pages/accounting/Sales";
import VendorsPage from "./pages/Vendor";
import CelebrationPage from "./pages/staff/Celebration";
import GuestLogin from "./pages/guestPortal/Login";
import GuestPortal from "./pages/guestPortal/Dashboard";
import ItemPage from "./pages/guestPortal/ServiceMenu";
import GuestBillPage from "./pages/guestPortal/Bill";
import ReviewsPage from "./pages/guestPortal/GuestReviewList";
import CheckAvailability from "./pages/bookings/CheckRoomAvailability";
import { LiveOrdersProvider } from "./context/LiveOrdersContext";
import ComplaintPage from "./pages/MaintenanceLog";
import PaymentProcessing from "./pages/VerifyTransaction";
import BookingSuccess from "./pages/bookings/OnlineBookingSuccess";
import CheckInPage from "./pages/bookings/ScannedBookings";

type InventoryType = "foods" | "drinks" | "games" | "laundries";

function InventoryRoute() {
  const { type } = useParams<{ type: InventoryType }>();

  switch (type) {
    case "foods":
    case "drinks":
      return <FoodDrinkInventoryPage />;
    case "games":
    case "laundries":
      return <GameLaundryInventoryPage />;
    default:
      return <Navigate to="/404" replace />;
  }
}

export default function App() {
  return (
    <NetworkProvider interval={3000}>
      <CartProvider>
        <ModalProvider>
          <ToastProvider>
            <Routes>

              {/* ---------- Guest Routes ---------- */}
              <Route path="/guests/login" element={<GuestLogin />} />
              <Route path="/guests/dashboard" element={<GuestPortal />} />
              <Route path="/guests/billing" element={<GuestBillPage />} />
              <Route path="/guests/menu/:type" element={<ItemPage />} />
              <Route path="/payment/verify" element={<PaymentProcessing />} />
              <Route path="/guests/booking/success" element={<BookingSuccess />} />

              {/* ---------- Public Routes ---------- */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/rooms/search" element={<CheckAvailability />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/forgot-password/success" element={<ForgotPasswordSuccessPage />} />
              <Route path="/:action/verify" element={<TokenVerificationPage />} />
              <Route path="/:action/invalid" element={<InvalidTokenPage />} />
              <Route path="/download" element={<InstallApp />} />
              <Route path="/celebration" element={< CelebrationPage />} />
              
              {/* Redirect root to login */}
              <Route path="/" element={<Navigate to="/login" replace />} />

              <Route path="/subscription" element={<SubscriptionExpired />} />
              <Route path="/test" element={<TestPage />} />

              {/* ---------- Protected Routes ---------- */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <LiveOrdersProvider>
                        <DashboardPage />
                      </LiveOrdersProvider>
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />


              <Route
                path="/vendors"
                element={
                  <ProtectedRoute>
                    <VendorsPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/service/drinks"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <Drinks />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/service/foods"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <Foods />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/service/games"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <Games />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/service/laundries"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <Laundry />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/orders"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <LiveOrdersProvider>
                        <OrderProvider>
                          <Orders />
                        </OrderProvider>
                      </LiveOrdersProvider>
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/rooms"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <Rooms />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/rooms/:action"
                element={
                  <ProtectedRoute>
                    <BookRoomPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/rooms/book/:action"
                element={
                  <ProtectedRoute>
                    <BookRoomPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/bookings"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <BookingPage />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />

              <Route path="/check-in/:billId" element={<CheckInPage />} />

              <Route
                path="/guests"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <GuestPage />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/reviews"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <ReviewsPage />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/staffs"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <User />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/staffs/register"
                element={
                  <ProtectedRoute>
                    {/* <DashboardLayout> */}
                    <CreateUser />
                    {/* </DashboardLayout> */}
                  </ProtectedRoute>
                }
              />

              <Route
                path="/staffs/update"
                element={
                  <ProtectedRoute>
                    <CreateUser />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/staffs/signup"
                element={
                  <ProtectedRoute>
                    <CreateUser />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/staffs/assign-id"
                element={
                  // <ProtectedRoute>
                  <AssignStaffIDPage />
                  // </ProtectedRoute>
                }
              />

              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <StaffProfile />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/staffs/update"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <CreateUser />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/inventory"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <InventoryPage />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/expenditures"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <Expenditures />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/sales"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <Sales />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/inventory/rooms"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <RoomsInventoryPage />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/inventory/:type"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <InventoryRoute />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/sales/:type"
                element={
                  <ProtectedRoute>
                    <DailySales />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/roster"
                element={
                  <ProtectedRoute>
                    <Roster />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <SettingsPage />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/maintenances"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <ComplaintPage />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/settings/receipt"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <PrintSettings />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />

              {/* ---------- 404 fallback ---------- */}
              <Route path="/404" element={<NotFoundPage />} />
              <Route path="*" element={<NotFoundPage />} />

            </Routes>
          </ToastProvider>
        </ModalProvider>
      </CartProvider>
    </NetworkProvider>
  );
}
